import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";

import { currencyFormatter, getProductsFiles, handleResponse } from "_helpers";
import { checkIfNumberOfPaymentAttemptsExceeded, increaseBillingCounter, resetBillingCounter } from "_helpers/billing";
import { ordersService } from "_services";
import { customerActions, ordersActions, constantActions } from "_store/actions";
import { REGISTER_TRADEMARK_CODE_ARRAY } from "_constants";

import { SpinnerBlock } from "widget/Spinner";
import Documents from "components/OrderDetail/Documents";

const Text = styled.div`
  font-family: "Montserrat";

  &:not(:first-child) {
    margin-top: 24px;
  }

  ${({ textCenter }) =>
    textCenter &&
    css`
      width: 100%;
      text-align: center;
    `}

  ${({ textJustify }) =>
    textJustify &&
    css`
      width: 100%;
      text-align: justify;
    `}

  ${({ spaceBetween }) =>
    spaceBetween &&
    css`
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 24px;
      align-items: end;
    `}
`;

const SummaryContainer = styled.div`
  padding: 10px;
  /* border-top: 1px solid gray; */
  box-shadow: 0 0 10px lightgray;
  border-radius: 4px;
`;

const StyledBody = styled(Modal.Body)`
  max-height: 80dvh;
  overflow-y: auto;
`;

const ListContainer = styled.div`
  margin-bottom: 24px;
  height: 100%;
  max-height: 100%;
`;

const ProductContainer = styled.div`
  display: flex;
  padding: 12px 4px;
  border-bottom: 1px solid #ecedf1;
  cursor: pointer;

  ${({ index }) =>
    index % 2 !== 0 &&
    css`
      background-color: #fafaff;
    `}

  &:hover {
    background-color: rgba(155, 174, 201, 0.1);
  }

  &:active {
    background-color: rgba(155, 174, 201, 0.25);
  }
`;
const DataCell = styled.div`
  flex: 11;
  &:last-child {
    flex: 1;
  }
`;

const ErrorBox = styled.div`
  padding: 8px;
  background-color: #f0f0f0;
  border-radius: 12px;
`;

const EmptyMessage = styled(Text)`
  color: #7b848d;
  font-weight: bold;
  text-align: center;
  font-family: "Montserrat";
`;

const STEPS = {
  init: "step-init",
  select: "step-select",
  upload: "step-upload",
  charge: "step-charge",
  loading: "step-loading",
  success: "step-success",
  error: "step-error",
};

const BASIC_PRICE = 99;
const PER_CLASS_PRICE = 180; // CRM-1087

function StatementOfUseUpsell(props) {
  const dispatch = useDispatch();
  const { onCancel } = props;

  const { ordersList, ordersListLoading, requestLoading } = useSelector(({ orders }) => orders);

  const [step, setStep] = useState(STEPS.select);
  const [error, setError] = useState("");
  const [orderId, setOrderId] = useState(null);
  const [newRegisterDetails, setNewRegisterDetails] = useState(null);

  const registerProductsList = ordersList.reduce((acc, order) => {
    const registerProduct = order?.products?.find((product) =>
      REGISTER_TRADEMARK_CODE_ARRAY.includes(product?.code?.code)
    );

    if (registerProduct) {
      return [...acc, { ...registerProduct, crmOrderId: order?.crmOrderId }];
    }

    return acc;
  }, []);

  const orderDetails = ordersList?.find((order) => order?.uid === orderId);
  const registerDetails = orderDetails?.products?.find((product) =>
    REGISTER_TRADEMARK_CODE_ARRAY.includes(product?.code?.code)
  );

  const productId = registerDetails?.id;
  const productsFiles = registerDetails && getProductsFiles([registerDetails], [], ["TRADEMARK_SPECIMEN"]);

  const hasSpecimenFiles = productsFiles?.length > 0;
  const hasWebsite = registerDetails?.website;

  const classesCodes = registerDetails?.classes?.reduce((acc, item) => {
    const code = item?.goodsCategory?.split("-")[0]?.trim();

    if (!acc.includes(code)) {
      return [...acc, code];
    }

    return acc;
  }, []);

  const classesCount = classesCodes?.length || 0;

  const total = BASIC_PRICE + PER_CLASS_PRICE * classesCount;

  function handleCloseModal({ update = false }) {
    if (!requestLoading) {
      setStep(STEPS.init);
      setError("");
      setNewRegisterDetails(null);
      setOrderId(null);
      onCancel();
      update && dispatch(ordersActions.getOrders());
    }
  }

  function handleCancelCallback() {
    setOrderId(null);
    setNewRegisterDetails(null);
  }

  const handleChangeProductRegister = ({ target: { name, value } }) => {
    let newValue = value;

    if (value === "") {
      newValue = null;
    }

    setNewRegisterDetails({ ...newRegisterDetails, [name]: newValue });
  };

  const handleUploadAndUpdate = (formData) => {
    dispatch(
      ordersActions.postSpecimenUpdateProductWithoutRefresh({
        orderId,
        productId,
        product: newRegisterDetails,
        formData,
        fileType: "TRADEMARK_SPECIMEN",
        handleCallback: () => {
          dispatch(ordersActions.getOrders());
          setStep(STEPS.charge);
        },
      })
    );
  };

  const handleUpdate = () => {
    dispatch(
      ordersActions.putProductWithoutRefresh({
        productId,
        product: newRegisterDetails,
        handleCallback: () => {
          dispatch(ordersActions.getOrders());
          setStep(STEPS.charge);
        },
      })
    );
  };

  async function createUpsell() {
    try {
      const isNumberOfPaymentAttemptsExceeded = checkIfNumberOfPaymentAttemptsExceeded();

      if (isNumberOfPaymentAttemptsExceeded) {
        throw new Error("Uh oh - your card on file has been declined. Please update your payment method to proceed");
      }

      const request = await ordersService.chargeStatementOfUseFee(registerDetails?.orderId, null, true);
      const requestStatus = request?.status;

      handleResponse(request)
        .then((response) => {
          if (requestStatus === 200) {
            if (!response?.success) {
              if (response?.message === "Card Declined") {
                increaseBillingCounter();
                setError("Uh oh - your card on file has been declined. Please update your payment method to proceed.");
              } else {
                setError(response?.message || "Unknown error");
              }

              setStep(STEPS.error);
            } else {
              resetBillingCounter();
              setStep(STEPS.success);
            }
          } else {
            setError(response?.toString() || "Unknown error");
            setStep(STEPS.error);
          }
        })
        .catch((error) => {
          setError(error?.toString() || "Unknown error");
          setStep(STEPS.error);
        });
    } catch (response) {
      setError(response?.toString() || "Unknown error");
      setStep(STEPS.error);
    }
  }

  useEffect(() => {
    dispatch(constantActions.getProductFileTypes(["TRADEMARK_RegisterTrademark", "TRADEMARK_RegisterTrademarkV2"]));
  }, []);

  useEffect(() => {
    if (orderId) {
      const regTmProduct = orderDetails?.products?.find((product) =>
        REGISTER_TRADEMARK_CODE_ARRAY.includes(product?.code?.code)
      );
      if (regTmProduct) {
        const productsFiles = getProductsFiles([regTmProduct], [], ["TRADEMARK_SPECIMEN"]);

        const hasSpecimenFiles = productsFiles?.length > 0;
        const hasWebsite = regTmProduct?.website;

        setNewRegisterDetails(regTmProduct);

        if (hasSpecimenFiles || hasWebsite) {
          setStep(STEPS.charge);
        } else {
          setStep(STEPS.upload);
        }
      }
    }
  }, [orderId, orderDetails]);

  useEffect(() => {
    dispatch(customerActions.getPaymentMethods());
  }, [dispatch]);

  switch (step) {
    case STEPS.select:
      return (
        <Modal centered show size="md" backdrop="static" onHide={() => handleCloseModal({ update: false })}>
          <Modal.Header closeButton>
            <Text textCenter>
              Which Trademark are you looking
              <br />
              to get a Statement of Use for?
            </Text>
          </Modal.Header>
          <StyledBody>
            <ListContainer>
              {registerProductsList.map((product, idx) => {
                return (
                  <ProductContainer
                    index={idx}
                    key={`regTm-option-${idx}`}
                    title={"#" + product?.crmOrderId}
                    onClick={() => {
                      setOrderId(product?.orderId);
                    }}
                  >
                    <DataCell>
                      <Text>
                        {product?.markDesc}
                        &nbsp;
                        <span style={{ color: "gray" }}>({product?.trademarkType})</span>
                      </Text>
                    </DataCell>
                    <DataCell>
                      <Text>
                        <span style={{ color: "#c0c0c0" }}>
                          {"#"}
                          {idx + 1}
                        </span>
                      </Text>
                    </DataCell>
                  </ProductContainer>
                );
              })}
              {ordersListLoading && <SpinnerBlock />}
              {!ordersListLoading && registerProductsList?.length < 1 && (
                <EmptyMessage>No relevant trademarks were found.</EmptyMessage>
              )}
            </ListContainer>
          </StyledBody>
        </Modal>
      );

    case STEPS.upload:
      return (
        <Modal centered show size="lg" backdrop="static" onHide={() => handleCloseModal({ update: false })}>
          <Modal.Header closeButton>
            <Text>Trademark Statement of Use</Text>
          </Modal.Header>
          <Modal.Body>
            <>
              <Text textJustify>
                <p>We will need a specimen. Per the USPTO -</p>
                <p>
                  &quot;A specimen is a sample of your trademark as used in commerce. It is real-life evidence of how
                  you are actually using your trademark in the marketplace with the goods or services in your
                  application or registration maintenance filing&quot;.
                </p>
                <p>In many cases, if you have a website for your brand, we are able to take a specimen from there.</p>
                <p>Other acceptable specimens are:</p>
                <ul>
                  <li>a photograph showing your trademark on a label or hangtag attached to yours goods.</li>
                  <li>a photo of your trademark on the packaging for your goods or on the goods themselves.</li>
                </ul>
              </Text>
              {ordersListLoading ? (
                <SpinnerBlock />
              ) : (
                <Documents
                  showCancel
                  actions="SPECIMEN"
                  isDisabled={!!requestLoading}
                  order={orderDetails}
                  orderId={orderId}
                  product={newRegisterDetails}
                  productId={productId}
                  files={productsFiles}
                  onChange={handleChangeProductRegister}
                  onUpload={handleUploadAndUpdate}
                  onUpdate={handleUpdate}
                  handleCancelCallback={() => {
                    handleCancelCallback();
                    setStep(STEPS.select);
                  }}
                />
              )}
            </>
          </Modal.Body>
        </Modal>
      );

    case STEPS.charge:
      return (
        <Modal centered show size="md" backdrop="static" onHide={() => handleCloseModal({ update: false })}>
          <Modal.Header closeButton>
            <Text>Trademark Statement of Use</Text>
          </Modal.Header>
          <Modal.Body>
            <Text>
              <p>Let us help you get your application moving to registration!</p>
              <p>
                Our Statement of Use filing services are <b>$99 + ${PER_CLASS_PRICE} in USPTO fee per class</b>.
              </p>
            </Text>
            <div style={{ marginTop: "24px" }}>
              <SummaryContainer>
                <Text spaceBetween>
                  Statement of Use Service: <b>{currencyFormatter(BASIC_PRICE)}</b>
                </Text>
                <Text spaceBetween>
                  USPTO Fees (${PER_CLASS_PRICE} per class):
                  <b>
                    {currencyFormatter(PER_CLASS_PRICE)}&nbsp;X&nbsp;{classesCount}
                  </b>
                </Text>
                <hr />
                <Text spaceBetween>
                  Total: <b>{currencyFormatter(total)}</b>
                </Text>
              </SummaryContainer>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                handleCancelCallback();

                if (hasSpecimenFiles || hasWebsite) {
                  setStep(STEPS.select);
                } else {
                  setStep(STEPS.upload);
                }
              }}
            >
              Back
            </Button>
            <Button
              type="button"
              variant="success"
              disabled={total <= 0 || classesCount?.length < 1}
              onClick={() => {
                setStep(STEPS.loading);
                createUpsell();
              }}
            >
              Charge
            </Button>
          </Modal.Footer>
        </Modal>
      );

    case STEPS.success:
      return (
        <Modal centered show size="md" backdrop="static" onHide={() => handleCloseModal({ update: true })}>
          <Modal.Header closeButton>
            <Text>Trademark Statement of Use</Text>
          </Modal.Header>
          <Modal.Body>
            <Text>Thank you! Statement of Use request has been placed.</Text>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="success" onClick={() => handleCloseModal({ update: true })}>
              OK
            </Button>
          </Modal.Footer>
        </Modal>
      );

    case STEPS.error:
      return (
        <Modal centered show size="md" backdrop="static" onHide={() => handleCloseModal({ update: true })}>
          <Modal.Header closeButton>
            <Text>Trademark Statement of Use</Text>
          </Modal.Header>
          <Modal.Body>
            <ErrorBox>
              <div style={{ color: "red" }}>
                <b>Error:</b>
              </div>
              <div>{error}</div>
            </ErrorBox>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="success" onClick={() => handleCloseModal({ update: true })}>
              OK
            </Button>
          </Modal.Footer>
        </Modal>
      );

    case STEPS.loading:
      return (
        <Modal centered show size="md" backdrop="static" onHide={() => {}}>
          <Modal.Header>
            <Text>Trademark Statement of Use</Text>
          </Modal.Header>
          <Modal.Body>
            <SpinnerBlock />
          </Modal.Body>
        </Modal>
      );

    default:
      return null;
  }
}

StatementOfUseUpsell.propTypes = {
  upsellInfo: PropTypes.object,
  onCancel: PropTypes.func,
};

export default StatementOfUseUpsell;
